/**
 * Hebrew calendar modifier, from Hebcal — free, no API key.
 *
 * This is structural in Tel Aviv, not a garnish. Candle-lighting empties a large part
 * of the map; havdalah refills it within an hour. Chag eves behave like Fridays, and
 * the intermediate days of Pesach and Sukkot have their own rhythm entirely.
 *
 * The multipliers here capture demand *per open venue*, not how many venues are open —
 * closures are already handled by each venue's opening-hours bitmap. The two compound:
 * fewer kitchens serving more hungry people is precisely why Friday night pays well
 * despite half the city being shut.
 *
 * A full year is fetched and cached, so the whole thing works offline for months.
 */

import { SHABBAT } from '../scoring/weights.js';
import { fetchWithFallback } from './cache.js';

/** Tel Aviv-Yafo in the GeoNames database, which is what Hebcal keys locations on. */
const TLV_GEONAME_ID = 293397;

/** A year of calendar data is worth keeping for a month before refreshing. */
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

/** A period during which the city is in Shabbat or chag mode. */
export interface RestWindow {
  /** Epoch milliseconds. */
  start: number;
  end: number;
  label: string;
}

interface HebcalItem {
  title: string;
  date: string;
  category: string;
}

async function fetchCalendar(year: number): Promise<RestWindow[]> {
  const url =
    'https://www.hebcal.com/hebcal?v=1&cfg=json' +
    `&year=${year}&month=x` +
    '&maj=on&min=off&mod=off&nx=off&ss=off&mf=off' +
    '&c=on&geo=geoname&geonameid=' +
    TLV_GEONAME_ID +
    '&M=on&s=off';

  const response = await fetch(url, { signal: AbortSignal.timeout(10000) });
  if (!response.ok) throw new Error(`Hebcal returned ${response.status}`);

  const data = (await response.json()) as { items: HebcalItem[] };
  return pairCandlesWithHavdalah(data.items);
}

/**
 * Turn a flat stream of candle-lighting and havdalah events into closed intervals.
 *
 * Hebcal emits these as separate items in date order. A candle-lighting with no
 * following havdalah (the last event of the year) is dropped rather than left open —
 * an unterminated window would apply the multiplier indefinitely.
 */
export function pairCandlesWithHavdalah(items: readonly HebcalItem[]): RestWindow[] {
  const windows: RestWindow[] = [];
  let pendingStart: { time: number; label: string } | null = null;

  for (const item of items) {
    const time = Date.parse(item.date);
    if (Number.isNaN(time)) continue;

    if (item.category === 'candles') {
      // Consecutive candle-lightings happen when a chag runs straight into Shabbat;
      // keep the earliest so the whole stretch is covered.
      if (pendingStart === null) pendingStart = { time, label: item.title };
    } else if (item.category === 'havdalah') {
      if (pendingStart !== null) {
        windows.push({ start: pendingStart.time, end: time, label: pendingStart.label });
        pendingStart = null;
      }
    }
  }

  return windows;
}

export interface CalendarState {
  multiplier: number;
  label: string;
  source: 'cache' | 'network' | 'stale' | 'none';
}

export const NEUTRAL_CALENDAR: CalendarState = {
  multiplier: 1,
  label: '',
  source: 'none',
};

/**
 * Multiplier for a given instant, given the known rest windows.
 *
 * Three regimes: inside a rest window (fewer venues, more demand each), the few hours
 * after it ends (everything reopens at once and the queue floods), and ordinary time.
 */
export function multiplierForInstant(
  windows: readonly RestWindow[],
  at: number,
): { multiplier: number; label: string } {
  for (const window of windows) {
    if (at >= window.start && at < window.end) {
      return { multiplier: SHABBAT.duringMultiplier, label: `${window.label} — fewer venues, more demand each` };
    }
  }

  // The motzash surge: the window has just ended and the city switches back on.
  const surgeMs = (SHABBAT.motzashEndMin - SHABBAT.motzashStartMin) * 60 * 1000;
  for (const window of windows) {
    if (at >= window.end && at < window.end + surgeMs) {
      return { multiplier: SHABBAT.motzashMultiplier, label: 'מוצ״ש — the city comes back on' };
    }
  }

  return { multiplier: 1, label: '' };
}

export async function loadCalendar(now: Date): Promise<{
  state: CalendarState;
  windows: RestWindow[];
}> {
  const year = now.getFullYear();
  const { value, source } = await fetchWithFallback(`hebcal:${year}`, MAX_AGE_MS, () =>
    fetchCalendar(year),
  );

  if (!value) {
    return { state: NEUTRAL_CALENDAR, windows: [] };
  }

  const { multiplier, label } = multiplierForInstant(value, now.getTime());
  return {
    state: { multiplier, label, source },
    windows: value,
  };
}
